import * as React from "react";
import ReactDataSheet from "react-datasheet";
import styled from "styled-components";
import { cloneDeep, debounce } from "lodash";

import {
  IGridElement,
  ISheetState,
  ISheetProps,
  IRowValidation,
  ITargetUrlOptions,
} from "../types";

import API from "src/services/api";
import { measureTextWidth } from "src/services/links/measureTextWidth";
import { Button, Input, Icons, Tooltip } from "src/components";
import SelectEditor from "./SelectEditor";
import TextEditor from "./TextEditor";
import { Sidebar } from "./Sidebar";
import { Tooltips } from "./Tooltips";
import { createRow } from "./services/createRow";
import {
  getTableWidth,
  prepareHeaders,
  resizeHeader,
} from "./services/headers";
import { handleCellChanges } from "./services/handleCellChanges";
import { getValidationData } from "./services/getValidationData";
import { fixScrollPosition } from "./services/fixScrollPosition";
import { validateDuplicateRows } from "./services/validateDuplicateRows";
import {
  hasError,
  isDuplicate,
  isValid,
  validationMessages,
} from "./services/validationMessages";
import { callQueue } from "./services/callQueue";
import { mapLinkToApi } from "./services/mapLinkToApi";
import { markRowErrors } from "./services/markRowErrors";

const StyledHeader = styled.div`
  background-color: ${(props) => props.theme.primary};
`;

const HEADER_WIDTH_COMPENSATION = 2;

class Sheet extends React.Component<ISheetProps, ISheetState> {
  state: ISheetState = {
    grid: [],
    headers: [],
    tableWidth: 0,
    validation: [],
    targetUrls: [],
    checkedRows: [],
    addRowsCount: 1,
    dragging: false,
  };

  containerRef = React.createRef<HTMLDivElement>();
  headerRef = React.createRef<HTMLDivElement>();
  selectPortalRef = React.createRef<HTMLDivElement>();

  dataEditor = (props: ReactDataSheet.DataEditorProps<IGridElement>) => {
    if (props.cell.type === "select") {
      return (
        <SelectEditor portalElement={this.selectPortalRef.current} {...props} />
      );
    }
    if (props.cell.type === "date") {
      return <TextEditor type="date" {...props} />;
    }
    return <TextEditor {...props} />;
  };

  componentDidMount() {
    const headers = prepareHeaders(this.props.parameterData);
    this.setState(
      {
        headers,
        tableWidth: getTableWidth(headers),
      },
      () => {
        const { initialLinks } = this.props;
        if (initialLinks) {
          const newRows = initialLinks.map((link) =>
            createRow(this.state.headers, link)
          );
          const newValidationEntry: IRowValidation = {
            validating: false,
            messages: [],
          };
          const newValidations = Array.from(
            { length: initialLinks.length },
            () => cloneDeep(newValidationEntry)
          );
          this.setState((state) => {
            const newGrid = state.grid.concat(newRows);
            const newValidation = state.validation.concat(newValidations);
            const validation = validateDuplicateRows(newGrid, newValidation);

            return {
              grid: newGrid,
              validation,
            };
          }, this.startTargetChecks);
        } else {
          this.addEmptyRow();
        }
      }
    );
  }
  componentWillUnmount() {
    this.state.validation.forEach((v) => {
      if (v.cancel) {
        v.cancel();
      }
    });
    this.state.targetUrls.forEach((t) => {
      if (t.cancel) {
        t.cancel();
      }
    });
  }

  resizeHeader = (index: number) => () => {
    this.setState((state) => {
      const headers = resizeHeader(index, state.headers, state.grid);
      return {
        grid: cloneDeep(state.grid),
        headers,
        tableWidth: getTableWidth(headers),
      };
    });
  };

  changeAddRowsCount: React.FormEventHandler<HTMLInputElement> = (event) => {
    this.setState({
      addRowsCount: Number(event.currentTarget.value),
    });
  };

  addEmptyRow = (count?: number) => {
    const newRow = createRow(this.state.headers);
    const newValidationEntry: IRowValidation = {
      validating: false,
      messages: [],
    };
    this.setState((state) => {
      const newRows = Array.from({ length: count || state.addRowsCount }, () =>
        cloneDeep(newRow)
      );
      const newValidations = Array.from(
        { length: count || state.addRowsCount },
        () => cloneDeep(newValidationEntry)
      );
      const newGrid = state.grid.concat(newRows);
      const newValidation = state.validation.concat(newValidations);
      const validation = validateDuplicateRows(newGrid, newValidation);

      return {
        grid: newGrid,
        validation,
        addRowsCount: 1,
      };
    });
  };

  validateAll = () => {
    this.state.grid.forEach((_, i) => this.validateRow(i)());
  };

  updateRowValidation = (row: number, data: Partial<IRowValidation>) => {
    this.setState((state) => ({
      validation: [
        ...state.validation.slice(0, row),
        {
          ...state.validation[row],
          ...data,
        },
        ...state.validation.slice(row + 1),
      ],
    }));
  };

  validateRow = (row: number) => async () => {
    this.props.onChange();
    try {
      const rowData = this.state.grid[row];
      const currentValidationData = getValidationData(rowData);
      if (
        currentValidationData === this.state.validation[row].lastValidationData
      ) {
        return;
      }

      const linkData = mapLinkToApi(this.state.headers, rowData);

      const { getData, cancel } = API.LINK.validate({
        linkCreationRequestParameters: linkData,
        skipChangedParameterCheck: true,
      });
      this.updateRowValidation(row, {
        validating: true,
        cancel,
        lastValidationData: currentValidationData,
        messages: this.state.validation[row].messages.filter(
          (m) =>
            ![
              validationMessages.OK,
              validationMessages.ERROR,
              validationMessages.VALIDATION,
            ].includes(m)
        ),
      });

      const {
        isLinkValid,
        missingParameterIds,
        invalidParameterIds,
        linkCreationRequestParameters,
        duplicateLink,
      } = await getData();

      this.updateRowValidation(row, {
        messages:
          isLinkValid && !duplicateLink
            ? [...this.state.validation[row].messages, validationMessages.OK]
            : [
                duplicateLink
                  ? validationMessages.DUPLICATE
                  : validationMessages.ERROR,
                ...this.state.validation[row].messages,
              ],
      });
      this.setState((state) => ({
        grid: state.grid.map((r, index) => {
          if (index !== row) {
            return r;
          }
          return markRowErrors({
            row: r,
            headers: state.headers,
            missingParameterIds: missingParameterIds || [],
            invalidParameterIds: invalidParameterIds || [],
            linkCreationRequestParameters,
          });
        }),
      }));
    } catch (error) {
      console.log(error);
    } finally {
      this.updateRowValidation(row, {
        validating: false,
        cancel: undefined,
      });
    }
  };

  sheetRenderer: ReactDataSheet.SheetRenderer<IGridElement> = (props) => {
    const anyRowChecked = this.state.checkedRows.length > 0;
    const allRowsChecked =
      this.state.checkedRows.length === this.state.grid.length;
    return (
      <>
        <div ref={this.selectPortalRef} />
        <div
          ref={this.containerRef}
          className={props.className + " overflow-x-auto max-w-full pb-2"}
        >
          <div
            style={{ width: this.state.tableWidth + "rem" }}
            className="relative bg-grey-lighter"
          >
            {
              <div className="absolute l-0 h-8" style={{ top: "-2rem" }}>
                <Tooltip
                  text="Copy rows"
                  position="bottom"
                  side="left"
                  className="text-xs mr-2"
                >
                  <button
                    onClick={this.copyCheckedRows}
                    className={anyRowChecked ? "" : "opacity-50"}
                    disabled={!anyRowChecked}
                  >
                    <Icons.Copy />
                  </button>
                </Tooltip>
                <Tooltip
                  text="Delete rows"
                  position="bottom"
                  side="left"
                  className="text-xs"
                >
                  <button
                    onClick={this.deleteCheckedRows}
                    disabled={!anyRowChecked}
                    className={anyRowChecked ? "" : "opacity-50"}
                  >
                    <Icons.Trash className="w-4" />
                  </button>
                </Tooltip>
              </div>
            }
            <div
              ref={this.headerRef}
              className="flex bg-grey-dark border-b border-white"
            >
              <Tooltip
                text="Select all rows"
                position="bottom"
                side="left"
                className="text-xs"
              >
                <label
                  className={`relative flex items-center justify-end w-8 h-full px-2 ${
                    allRowsChecked ? "bg-blue text-white" : "bg-grey-dark"
                  } cursor-pointer`}
                >
                  <input
                    onChange={this.toggleAllCheckedRows}
                    type="checkbox"
                    className="absolute opacity-0 pointer-events-none"
                  />
                  <Icons.Checkbox />
                </label>
              </Tooltip>
              {this.state.headers.map((header, index) => (
                <div key={header.label} style={{ width: header.width + "rem" }}>
                  <StyledHeader className="relative text-white font-bold h-8 p-2 pr-6 border-r border-white">
                    {header.label}
                    {header.mandatory && "*"}
                    {!(header.options && header.options.length > 0) && (
                      <button
                        onClick={this.resizeHeader(index)}
                        className="absolute pin-t pin-r h-full text-white opacity-50 hover:opacity-100 focus:outline-none"
                      >
                        <Icons.Stretch />
                      </button>
                    )}
                  </StyledHeader>
                </div>
              ))}
            </div>
            {props.children}
          </div>
        </div>
      </>
    );
  };

  rowRenderer: ReactDataSheet.RowRenderer<IGridElement> = (props) => {
    const messages = this.state.validation[props.row].messages;
    const isChecked = this.state.checkedRows.includes(props.row);
    let rowColor = "bg-grey-lighter";
    if (isValid(messages)) {
      rowColor = "bg-green-light";
    }
    if (hasError(messages)) {
      rowColor = "bg-red-light";
    }
    if (isDuplicate(messages)) {
      rowColor = "bg-yellow-light";
    }
    return (
      <div className={`flex ${rowColor} border-b border-white`}>
        <label
          className={`relative flex items-center justify-end w-8 px-2 ${
            isChecked ? "bg-blue text-white" : "bg-grey-dark"
          } text-sm font-mono cursor-pointer`}
        >
          <input
            onChange={this.toggleCheckedRow(props.row)}
            type="checkbox"
            className="absolute opacity-0 pointer-events-none"
          />
          {props.row + 1}
        </label>
        {props.children}
      </div>
    );
  };

  cellRenderer: ReactDataSheet.CellRenderer<IGridElement> = (props) => {
    const {
      attributesRenderer,
      row,
      col,
      cell,
      selected,
      editing,
      updated,
      onMouseDown,
      onMouseOver,
      style,
      ...rest
    } = props;

    const mouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      this.setState({
        dragging: true,
      });
      this.fixScrollPosition(col);
      onMouseDown(event);
    };
    const mouseUp = () => {
      this.setState({
        dragging: false,
      });
    };
    const mouseOver = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (this.state.dragging) {
        this.debouncedScrollFix(col, 50);
      }
      onMouseOver(event);
    };

    const value = this.valueRenderer(cell, row, col);
    const cellWidth = this.state.headers[col].width;
    const valueWidth = !value
      ? 0
      : measureTextWidth(String(value)) + HEADER_WIDTH_COMPENSATION;
    const targetUrl =
      cell.isTargetUrl && this.state.targetUrls.find((t) => t.value === value);

    return (
      <div className="group relative" style={{ width: cellWidth + "rem" }}>
        <div
          {...rest}
          onMouseDown={mouseDown}
          onMouseUp={mouseUp}
          onMouseOver={mouseOver}
          style={style as React.CSSProperties}
          className={`h-8 border-r border-white select-none ${
            selected ? "bg-blue-light" : ""
          } ${cell.readOnly ? "bg-darken" : "cursor-cell"} ${
            editing ? "" : "flex justify-between items-center px-2"
          }`}
        >
          <div className="flex-1 overflow-hidden">
            <div className="whitespace-no-wrap break-all">{props.children}</div>
          </div>
          {!editing && (
            <Tooltips
              cell={cell}
              isTargetUrl={cell.isTargetUrl}
              targetUrl={targetUrl || undefined}
            />
          )}
        </div>
        {!cell.readOnly &&
          cell.type === "text" &&
          !editing &&
          valueWidth >= cellWidth && (
            <span className="p-2 bg-grey-light pointer-events-none shadow-lg absolute z-50 pin-t pin-l pin-r break-all hidden group-hover:block">
              {value ? String(value) : null}
            </span>
          )}
      </div>
    );
  };

  valueRenderer: ReactDataSheet.ValueRenderer<IGridElement> = (
    cell,
    row,
    col
  ) => {
    let value = cell.value;
    if (value !== null && (cell.readOnly || cell.type === "select")) {
      const option = this.state.headers[col].options.find(
        (o) => String(o.value.id) === cell.value
      );
      if (option) {
        value = option.label;
      }
    }
    return value;
  };

  onCellsChanged: ReactDataSheet.CellsChangedHandler<IGridElement, string> = (
    changes
  ) => {
    this.props.onChange();
    this.setState((state) => {
      const { grid, validation } = handleCellChanges(
        changes,
        state.grid,
        state.headers,
        state.validation
      );
      return { grid, validation };
    }, this.startTargetChecks);
  };

  startTargetChecks = () => {
    const newChecks: string[] = [];
    this.state.grid.forEach((row) =>
      row.forEach((cell) => {
        if (!cell.isTargetUrl || !cell.value) {
          return;
        }
        const existingUrl =
          newChecks.includes(cell.value) ||
          this.state.targetUrls.find((t) => t.value === cell.value);
        if (!existingUrl) {
          newChecks.push(cell.value);
          this.targetCheckQueue(cell.value);
        }
      })
    );
  };
  updateTargetUrl = (targetUrl: string, data: Partial<ITargetUrlOptions>) => {
    this.setState((state) => ({
      grid: cloneDeep(state.grid),
      targetUrls: state.targetUrls.map((t) =>
        t.value === targetUrl
          ? {
              ...t,
              ...data,
            }
          : t
      ),
    }));
  };
  targetCheck = async (targetUrl: string) => {
    this.setState((state) => ({
      targetUrls: [
        ...state.targetUrls,
        {
          value: targetUrl,
          checking: true,
        },
      ],
    }));
    try {
      const { getData, cancel } = API.LINK.TARGET_CHECK.url(targetUrl);
      this.updateTargetUrl(targetUrl, {
        cancel,
      });
      const data = await getData();
      this.updateTargetUrl(targetUrl, {
        checking: false,
        cancel: undefined,
        result: {
          success: data.httpCode === "ok" && data.redirects.length === 0,
          message: data.displayMessage,
        },
      });
    } catch (error) {
      this.updateTargetUrl(targetUrl, {
        checking: false,
        cancel: undefined,
        result: {
          success: false,
          message: "Invalid URL",
        },
      });
    }
  };
  targetCheckQueue = callQueue(this.targetCheck);

  isCellNavigable = (_: any, __: any, col: number) => {
    this.fixScrollPosition(col);
    return true;
  };

  fixScrollPosition = (col: number, compensation = 20) => {
    if (!this.headerRef.current || !this.containerRef.current) {
      return;
    }
    fixScrollPosition(
      this.containerRef.current,
      this.headerRef.current,
      col,
      compensation
    );
  };
  debouncedScrollFix = debounce(this.fixScrollPosition, 300);

  copyRows = (rows: number[]) => {
    this.setState((state) => {
      const newRows = rows.map((row) => cloneDeep(state.grid[row]));
      const newGrid = [...state.grid, ...newRows];
      const newRowsValidation = rows.map((row) =>
        cloneDeep(state.validation[row])
      );
      const newValidation = [...state.validation, ...newRowsValidation];
      const validation = validateDuplicateRows(newGrid, newValidation);
      return {
        grid: newGrid,
        validation,
      };
    });
  };
  copyRow = (row: number) => () => this.copyRows([row]);

  deleteRows = (rows: number[]) => {
    this.setState((state) => {
      const newGrid = state.grid.filter((_, r) => !rows.includes(r));
      const newValidation = state.validation.filter(
        (_, r) => !rows.includes(r)
      );
      const validation = validateDuplicateRows(newGrid, newValidation);
      return {
        grid: newGrid,
        validation,
      };
    }, this.maintainAboveOneRow);
  };
  deleteRow = (row: number) => () => this.deleteRows([row]);

  toggleCheckedRow = (row: number) => () => {
    this.setState((state) => ({
      grid: cloneDeep(state.grid),
      checkedRows: state.checkedRows.includes(row)
        ? state.checkedRows.filter((r) => r !== row)
        : [...state.checkedRows, row],
    }));
  };
  toggleAllCheckedRows = () => {
    this.setState((state) => ({
      grid: cloneDeep(state.grid),
      checkedRows:
        state.checkedRows.length === state.grid.length
          ? []
          : state.grid.map((_, i) => i),
    }));
  };
  copyCheckedRows = () => {
    this.copyRows(this.state.checkedRows);
    this.setState({
      checkedRows: [],
    });
  };
  deleteCheckedRows = () => {
    this.deleteRows(this.state.checkedRows);
    this.setState({
      checkedRows: [],
    });
  };

  maintainAboveOneRow = () => {
    if (this.state.grid.length === 0) {
      this.addEmptyRow(1);
    }
  };

  isEmptyRow = (row: IGridElement[]) =>
    row
      .filter((cell) => !cell.readOnly)
      .map((cell) => cell.value)
      .join("") === "";

  clearEmpty = () => {
    const rows = this.state.grid.reduce<number[]>((indices, row, index) => {
      if (this.isEmptyRow(row)) {
        indices.push(index);
      }
      return indices;
    }, []);
    this.deleteRows(rows);
  };

  handleAddRows = () => this.addEmptyRow();

  render() {
    const { grid, addRowsCount, validation, headers } = this.state;
    return (
      <div className="mb-16 mx-6">
        <div className="mb-16">
          <div className="flex">
            <ReactDataSheet
              data={this.state.grid}
              valueRenderer={this.valueRenderer}
              sheetRenderer={this.sheetRenderer}
              rowRenderer={this.rowRenderer}
              cellRenderer={this.cellRenderer}
              dataEditor={this.dataEditor}
              onCellsChanged={this.onCellsChanged}
              isCellNavigable={this.isCellNavigable}
              className="bulk-sheet mb-2 pt-12"
            />
            <Sidebar
              grid={this.state.grid}
              validation={this.state.validation}
              validateAll={this.validateAll}
              deleteRow={this.deleteRow}
              copyRow={this.copyRow}
              validateRow={this.validateRow}
            />
          </div>

          <Input
            value={addRowsCount}
            onChange={this.changeAddRowsCount}
            type="number"
            min="1"
            max={100 - grid.length}
            step="1"
            small={true}
            secondary={true}
            className="w-16 mr-2"
          />
          <Button
            onClick={this.handleAddRows}
            primary={true}
            size="small"
            className="mb-2 mr-2"
          >
            Add rows
          </Button>
          <Button
            onClick={this.clearEmpty}
            primary={true}
            size="small"
            className="mb-2"
          >
            Delete empty rows
          </Button>
        </div>

        {this.props.children && this.props.children(grid, validation, headers)}
      </div>
    );
  }
}

export default Sheet;



// WEBPACK FOOTER //
// ./src/views/LinkCreateBulk/components/Sheet.tsx